import {
    mdiAccountCashOutline,
    mdiAccountTie,
    mdiArrowDown,
    mdiArrowUp,
    mdiChevronDown,
    mdiChevronUp,
    mdiCodeBraces,
    mdiDelete,
    mdiDragVertical,
    mdiGavel,
    mdiMinusCircleOutline,
    mdiPlus,
} from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { BaseCheckbox, ISelectOption } from '../../base';
import { MESSAGE_CATEGORIES } from '../../../services/worker-configuration/automation/worker-automation.types';
import {
    AutomationWorker,
    RuleActionType,
} from '../../../services/worker-configuration/automation/worker-automation.types';
import { RULE_FIELD_HELPERS } from '../../../services/worker-configuration/automation/worker-automation.constants';
import { ReconsiderationDocument } from '../../../services/worker-configuration/automation/worker-automation.types';
import {
    ENTER_TASK_FEE_DISCLAIMER,
    getActionMeta,
    getAvailableRuleActionTypes,
    isEnterTaskMessageOwner,
    resolveRuleActionType,
    RULE_ACTION_SELECT_LABELS,
    RULE_NAME_MAX_LENGTH,
} from '../../../services/worker-configuration/automation/worker-automation.utils';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import {
    ConfigurationActionSummary,
    ConfigurationFieldError,
    ConfigurationFieldLabel,
    ConfigurationRequiredMark,
} from './configuration-action-summary';
import { ConfigurationAssignedUsers } from './configuration-assigned-users';
import { ConfigurationCoderRulesSelect } from './configuration-coder-rules-select';
import { ConfigurationFunctionBuilder } from './configuration-function-builder';
import { ConfigurationFunctionEditor } from './configuration-function-editor';
import { ConfigurationReconsiderationDocuments } from './configuration-reconsideration-documents';
import { ConfigurationRuleMatchBlock } from './configuration-rule-match-block';
import * as S from './configuration-rule.styles';
import { RuleTableRow } from './configuration-rules-table.utils';
import { SegmentButton, SegmentedControl } from '../detail/option-field.styles';

interface IConfigurationRuleCardProps {
    /** False while the list is searched, filtered or grouped — the visible order is not the priority. */
    canReorder: boolean;
    columns: string;
    disabled?: boolean;
    row: RuleTableRow;
    showCodeColumn: boolean;
    store: WorkerStore;
    worker: AutomationWorker;
}

function getActionBadgeVariant(type: RuleActionType): 'attention' | 'neutral' | 'primary' | 'success' {
    switch (type) {
        case 'SWITCH_TO_PRIVATE_PAY':
            return 'attention';
        case 'CREATE_APPEAL':
        case 'EXECUTE_ENTER_FUNCTION':
            return 'primary';
        case 'CREATE_MESSAGE':
            return 'success';
        default:
            return 'neutral';
    }
}

function getActionIcon(type: RuleActionType): string {
    switch (type) {
        case 'SWITCH_TO_PRIVATE_PAY':
            return mdiAccountCashOutline;
        case 'CREATE_APPEAL':
            return mdiGavel;
        case 'CREATE_MESSAGE':
            return mdiAccountTie;
        case 'EXECUTE_ENTER_FUNCTION':
            return mdiCodeBraces;
        default:
            return mdiMinusCircleOutline;
    }
}

export const ConfigurationRuleCard: React.FC<IConfigurationRuleCardProps> = observer(
    ({ canReorder, columns, disabled, row, showCodeColumn, store, worker }) => {
        const { rule } = row;
        const draggable = !disabled && canReorder;
        const [isCreatingCategory, setIsCreatingCategory] = React.useState(false);
        const [categoryDraft, setCategoryDraft] = React.useState('');
        const expanded = store.expandedRuleId === rule.id;
        const isDragging = store.draggingRuleId === rule.id;
        const isDragOver =
            store.dragOverRuleId === rule.id && Boolean(store.draggingRuleId) && store.draggingRuleId !== rule.id;
        const actionMeta = getActionMeta(rule.action.type);
        const resolvedActionType = resolveRuleActionType(rule.action.type);
        const actionValue = rule.action.value;
        const availableScopes = store.getAvailableMatchScopesForWorker();
        const reconsiderationDocuments = (actionValue.documents as ReconsiderationDocument[] | undefined) ?? [];

        const actionSelectOptions = getAvailableRuleActionTypes(worker.id).map(value => ({
            label: RULE_ACTION_SELECT_LABELS[value],
            value,
        }));
        const selectedActionOption =
            actionSelectOptions.find(option => option.value === rule.action.type) ?? actionSelectOptions[0];
        const configuredCategory = String(actionValue.messageCategory ?? '').trim();
        const messageCategoryValues = Array.from(
            new Set(
                [...MESSAGE_CATEGORIES, configuredCategory]
                    .map(category => String(category).trim())
                    .filter(Boolean),
            ),
        );
        const messageCategoryOptions: ISelectOption<string>[] = messageCategoryValues.map(category => ({
            label: category,
            value: category,
        }));
        const configuredCategoryOrFallback = configuredCategory || 'PAYER_FOLLOWUP';
        const selectedMessageCategory =
            messageCategoryOptions.find(option => option.value === configuredCategoryOrFallback) ?? messageCategoryOptions[0];
        const isEnterTaskOwner = isEnterTaskMessageOwner(actionValue.messageOwner);
        const ruleNameError = store.getRuleNameError(rule.id, rule.name);
        const actionError = store.getRuleActionError(rule.id);
        const canSubmitCategory = categoryDraft.trim().length > 0;

        const closeCategoryComposer = (): void => {
            setIsCreatingCategory(false);
            setCategoryDraft('');
        };

        const createMessageCategory = (): void => {
            const nextCategory = categoryDraft.trim();
            if (!nextCategory || disabled) {
                return;
            }
            store.setRuleActionValue(rule.id, 'messageCategory', nextCategory);
            closeCategoryComposer();
        };

        return (
            <S.RuleCard dragging={isDragging} dragOver={isDragOver}>
                <S.RuleHeader
                    type="button"
                    columns={columns}
                    data-allow-interaction="true"
                    aria-expanded={expanded}
                    onClick={() => store.toggleRuleExpanded(rule.id)}
                    draggable={draggable}
                    onDragStart={event => {
                        if (!draggable) {
                            return;
                        }
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', rule.id);
                        store.setDraggingRule(rule.id);
                    }}
                    onDragOver={event => {
                        if (!store.draggingRuleId) {
                            return;
                        }
                        event.preventDefault();
                        store.setDragOverRule(rule.id);
                    }}
                    onDrop={event => {
                        event.preventDefault();
                        store.reorderRule(rule.id);
                    }}
                    onDragEnd={() => store.setDraggingRule(null)}>
                    <span>
                        {!disabled && (
                            <S.DragHandle
                                locked={!canReorder}
                                title={canReorder ? undefined : 'Reorder disabled while filtered or grouped'}
                                onClick={event => event.stopPropagation()}>
                                <Icon path={mdiDragVertical} size={0.85} aria-hidden={true} />
                            </S.DragHandle>
                        )}
                    </span>
                    <S.PriorityBadge>{row.priority}</S.PriorityBadge>
                    <S.RuleName title={rule.name}>{rule.name}</S.RuleName>
                    {showCodeColumn && (
                        <S.RuleChips>
                            {row.codeChips.length ? (
                                row.codeChips.map((chip, chipIndex) => (
                                    <S.RuleCodeChip key={`${chip.label}-${chipIndex}`} kind={chip.kind}>
                                        {chip.label}
                                    </S.RuleCodeChip>
                                ))
                            ) : (
                                <S.RuleCellEmpty>—</S.RuleCellEmpty>
                            )}
                        </S.RuleChips>
                    )}
                    <S.RuleCell title={row.otherConditions.join('  AND  ') || undefined}>
                        {row.otherConditions.length ? (
                            row.otherConditions.join(' · ')
                        ) : (
                            <S.RuleCellEmpty>—</S.RuleCellEmpty>
                        )}
                    </S.RuleCell>
                    <S.ActionBadge variant={getActionBadgeVariant(rule.action.type)}>
                        <Icon path={getActionIcon(rule.action.type)} size={0.65} aria-hidden={true} />
                        {actionMeta.label}
                    </S.ActionBadge>
                    <Icon
                        path={expanded ? mdiChevronUp : mdiChevronDown}
                        size={0.9}
                        color="#8e9293"
                        aria-hidden={true}
                    />
                </S.RuleHeader>

                {expanded && (
                    <S.RuleBody data-readonly={disabled ? 'true' : undefined}>
                        <div style={{ marginBottom: 18 }}>
                            <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.ruleName}>
                                Rule Name (This is the Task Category)
                            </ConfigurationFieldLabel>
                            <S.FieldInput
                                value={rule.name}
                                disabled={disabled}
                                readOnly={disabled}
                                maxLength={RULE_NAME_MAX_LENGTH}
                                onChange={event => {
                                    if (!disabled) {
                                        store.renameRule(rule.id, event.target.value);
                                    }
                                }}
                            />
                            <ConfigurationFieldError message={ruleNameError} />
                        </div>

                        <S.SectionLabel>
                            <S.WhenTag>WHEN</S.WhenTag>
                            <S.SectionHint>all match blocks must match (AND)</S.SectionHint>
                        </S.SectionLabel>

                        <S.MatchBlockStack>
                            {rule.match.map((match, matchIndex) => (
                                <ConfigurationRuleMatchBlock
                                    key={`${rule.id}-${matchIndex}-${match.scope}`}
                                    rule={rule}
                                    match={match}
                                    matchIndex={matchIndex}
                                    removable={!disabled && rule.match.length > 1}
                                    availableScopes={availableScopes}
                                    store={store}
                                    disabled={disabled}
                                />
                            ))}
                        </S.MatchBlockStack>

                        {!disabled && (
                            <S.AddMatchButton
                                type="button"
                                disabled={disabled}
                                onClick={() => store.addMatchBlock(rule.id)}>
                                <Icon path={mdiPlus} size={0.75} aria-hidden={true} />
                                Add condition (AND)
                            </S.AddMatchButton>
                        )}

                        <S.SectionLabel>
                            <S.ThenTag>THEN</S.ThenTag>
                            <S.SectionHint>route to action bucket</S.SectionHint>
                        </S.SectionLabel>

                        <S.ActionPanel>
                            <S.ActionRow>
                                <S.FieldLabel style={{ marginBottom: 0, flex: 'none' }}>Action</S.FieldLabel>
                                <S.FieldSelect
                                    options={actionSelectOptions}
                                    value={selectedActionOption}
                                    required
                                    disabled={disabled}
                                    onChange={selected => {
                                        const option = selected as ISelectOption<string> | null;
                                        if (option?.value) {
                                            store.changeRuleAction(rule.id, option.value as RuleActionType);
                                        }
                                    }}
                                />
                            </S.ActionRow>

                            <ConfigurationActionSummary actionType={rule.action.type} />

                            {resolvedActionType === 'CLOSE_OUT' && (
                                <div>
                                    <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.closeoutDescription}>
                                        Closeout reason
                                    </ConfigurationFieldLabel>
                                    <S.FieldInput
                                        type="text"
                                        maxLength={45}
                                        disabled={disabled}
                                        readOnly={disabled}
                                        value={String(actionValue.closeoutDescription ?? '')}
                                        onChange={event =>
                                            store.setRuleActionValue(
                                                rule.id,
                                                'closeoutDescription',
                                                event.target.value.slice(0, 45),
                                            )
                                        }
                                    />
                                </div>
                            )}

                            {resolvedActionType === 'SWITCH_TO_PRIVATE_PAY' && (
                                <div>
                                    <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.memo} optional>
                                        Invoice memo
                                    </ConfigurationFieldLabel>
                                    <S.FieldTextarea
                                        rows={3}
                                        disabled={disabled}
                                        value={String(actionValue.memo ?? '')}
                                        onChange={event =>
                                            store.setRuleActionValue(rule.id, 'memo', event.target.value)
                                        }
                                    />
                                </div>
                            )}

                            {resolvedActionType === 'CREATE_APPEAL' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    <div>
                                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.prompt} optional>
                                            Customize the cover letter
                                        </ConfigurationFieldLabel>
                                        <S.FieldTextarea
                                            rows={3}
                                            disabled={disabled}
                                            value={String(actionValue.prompt ?? '')}
                                            onChange={event =>
                                                store.setRuleActionValue(rule.id, 'prompt', event.target.value)
                                            }
                                        />
                                    </div>

                                    <ConfigurationReconsiderationDocuments
                                        ruleId={rule.id}
                                        documents={reconsiderationDocuments}
                                        store={store}
                                        disabled={disabled}
                                    />
                                </div>
                            )}

                            {resolvedActionType === 'CREATE_MESSAGE' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    <div>
                                        <BaseCheckbox
                                            label="Group tasks for this rule"
                                            checked={rule.group === true}
                                            disabled={disabled}
                                            onChange={event => store.setRuleGrouped(rule.id, event.target.checked)}
                                        />
                                        <S.FieldHelper style={{ marginTop: 4 }}>
                                            {RULE_FIELD_HELPERS.group}
                                        </S.FieldHelper>
                                    </div>
                                    <div>
                                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.messageOwner}>
                                            Responsibility owner
                                        </ConfigurationFieldLabel>
                                        <SegmentedControl aria-disabled={disabled}>
                                            <SegmentButton
                                                type="button"
                                                active={actionValue.messageOwner !== 'PROVIDER'}
                                                disabled={disabled}
                                                onClick={() => {
                                                    if (!disabled) {
                                                        store.setRuleActionValue(rule.id, 'messageOwner', 'ENTER');
                                                    }
                                                }}>
                                                ENTER
                                            </SegmentButton>
                                            <SegmentButton
                                                type="button"
                                                variant="neutral"
                                                active={actionValue.messageOwner === 'PROVIDER'}
                                                disabled={disabled}
                                                onClick={() => {
                                                    if (!disabled) {
                                                        store.setRuleActionValue(rule.id, 'messageOwner', 'PROVIDER');
                                                    }
                                                }}>
                                                PROVIDER
                                            </SegmentButton>
                                        </SegmentedControl>
                                    </div>

                                    <S.TwoColumnFields>
                                        <div>
                                            <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.messageCategory}>
                                                Category
                                            </ConfigurationFieldLabel>
                                            <S.CategorySelectRow>
                                                <S.CategorySelectWrap>
                                                    <S.FieldSelect
                                                        options={messageCategoryOptions}
                                                        value={selectedMessageCategory}
                                                        required
                                                        disabled={disabled}
                                                        onChange={selected => {
                                                            const option = selected as ISelectOption<string> | null;
                                                            if (option?.value) {
                                                                store.setRuleActionValue(
                                                                    rule.id,
                                                                    'messageCategory',
                                                                    option.value,
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </S.CategorySelectWrap>
                                                {isCreatingCategory ? (
                                                    <>
                                                        <S.CategoryCreateInput
                                                            type="text"
                                                            autoFocus
                                                            disabled={disabled}
                                                            placeholder="New category"
                                                            value={categoryDraft}
                                                            onChange={event => setCategoryDraft(event.target.value)}
                                                            onKeyDown={event => {
                                                                if (event.key === 'Enter') {
                                                                    event.preventDefault();
                                                                    createMessageCategory();
                                                                }
                                                                if (event.key === 'Escape') {
                                                                    event.preventDefault();
                                                                    closeCategoryComposer();
                                                                }
                                                            }}
                                                        />
                                                        <S.ConfirmCreateCategoryButton
                                                            type="button"
                                                            disabled={disabled || !canSubmitCategory}
                                                            onClick={createMessageCategory}>
                                                            Create
                                                        </S.ConfirmCreateCategoryButton>
                                                    </>
                                                ) : (
                                                    <S.CreateCategoryButton
                                                        type="button"
                                                        disabled={disabled}
                                                        onClick={() => {
                                                            if (!disabled) {
                                                                setIsCreatingCategory(true);
                                                            }
                                                        }}>
                                                        + Create new category
                                                    </S.CreateCategoryButton>
                                                )}
                                            </S.CategorySelectRow>
                                        </div>
                                        <div>
                                            <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.assigned}>
                                                Assigned
                                            </ConfigurationFieldLabel>
                                            <ConfigurationAssignedUsers
                                                disabled={disabled}
                                                loading={store.assignableUsersLoading}
                                                assignedBankUserIds={
                                                    (actionValue.assigned as string[] | undefined) ?? []
                                                }
                                                users={store.assignableUsers}
                                                onChange={bankUserIds =>
                                                    store.setAssignedUsers(rule.id, bankUserIds)
                                                }
                                            />
                                        </div>
                                    </S.TwoColumnFields>

                                    <div>
                                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.suggestedResolution}>
                                            Suggested next steps
                                        </ConfigurationFieldLabel>
                                        <S.FieldTextarea
                                            rows={2}
                                            disabled={disabled}
                                            readOnly={disabled}
                                            value={String(actionValue.suggestedResolution ?? '')}
                                            onChange={event =>
                                                store.setRuleActionValue(
                                                    rule.id,
                                                    'suggestedResolution',
                                                    event.target.value,
                                                )
                                            }
                                        />
                                        {isEnterTaskOwner && (
                                            <S.FieldHelper style={{ marginTop: 6, marginBottom: 0 }}>
                                                {ENTER_TASK_FEE_DISCLAIMER}
                                            </S.FieldHelper>
                                        )}
                                    </div>
                                </div>
                            )}

                            {resolvedActionType === 'EXECUTE_ENTER_FUNCTION' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    <ConfigurationFunctionBuilder
                                        ruleId={rule.id}
                                        store={store}
                                        disabled={disabled}
                                    />

                                    <div>
                                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.function}>
                                            Function
                                            <ConfigurationRequiredMark />
                                        </ConfigurationFieldLabel>
                                        <ConfigurationFunctionEditor
                                            // Also disabled while the AI build is in flight — it
                                            // unconditionally overwrites `function` with its result on
                                            // arrival, so a manual edit typed during that window would
                                            // otherwise be silently discarded (PR review, 2026-09-28).
                                            disabled={disabled || store.getFunctionBuilder(rule.id).building}
                                            value={String(actionValue.function ?? '')}
                                            onChange={value => store.setRuleActionValue(rule.id, 'function', value)}
                                        />
                                        <ConfigurationFieldError message={actionError} />
                                    </div>

                                    <div>
                                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.disableCodingRules}>
                                            Coder rules
                                        </ConfigurationFieldLabel>
                                        <ConfigurationCoderRulesSelect
                                            disabled={disabled}
                                            loading={store.coderRulesLoading}
                                            error={store.coderRulesError}
                                            coderRules={store.coderRules}
                                            disableCodingRules={actionValue.disableCodingRules}
                                            onChange={ruleNumbers => store.setDisabledCodingRules(rule.id, ruleNumbers)}
                                        />
                                    </div>
                                </div>
                            )}
                        </S.ActionPanel>

                        {!disabled && (
                            <S.RuleFooter>
                                <S.FooterButton
                                    type="button"
                                    disabled={disabled || !canReorder}
                                    title={canReorder ? undefined : 'Reorder disabled while filtered or grouped'}
                                    onClick={() => store.moveRule(rule.id, -1)}>
                                    <Icon path={mdiArrowUp} size={0.65} aria-hidden={true} />
                                    Up
                                </S.FooterButton>
                                <S.FooterButton
                                    type="button"
                                    disabled={disabled || !canReorder}
                                    title={canReorder ? undefined : 'Reorder disabled while filtered or grouped'}
                                    onClick={() => store.moveRule(rule.id, 1)}>
                                    <Icon path={mdiArrowDown} size={0.65} aria-hidden={true} />
                                    Down
                                </S.FooterButton>
                                <S.FooterSpacer />
                                <S.FooterButton
                                    type="button"
                                    danger
                                    disabled={disabled}
                                    onClick={() => store.deleteRule(rule.id)}>
                                    <Icon path={mdiDelete} size={0.65} aria-hidden={true} />
                                    Delete rule
                                </S.FooterButton>
                            </S.RuleFooter>
                        )}
                    </S.RuleBody>
                )}
            </S.RuleCard>
        );
    },
);
